import { base64ListRequest } from "@/services/backend/_lib/base64-list-request";
import { backendApi } from "@/services/backend/api";
import {
  DeletePestcontrolMonitorRequest,
  DeletePestcontrolMonitorImageRequest,
  PestcontrolMonitor,
  PestcontrolMonitorListRequest,
  PestcontrolMonitorListResponse,
  PutPestcontrolMonitorRequest,
  ShowPestcontrolMonitorImageRequest,
  ShowPestcontrolMonitorRequest,
  UploadPestcontrolMonitorImageRequest,
} from "@/services/backend/pestcontrol/types/monitor";

const pestcontrolMonitorApi = backendApi
  .enhanceEndpoints({
    addTagTypes: ["PestcontrolMonitor", "PestcontrolMonitors"],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      pestcontrolMonitorPut: build.mutation<
        PestcontrolMonitor,
        PutPestcontrolMonitorRequest
      >({
        query: (body) => ({
          url: "/pestcontrol/monitors",
          method: "PUT",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "PestcontrolMonitor", id: request.monitor.id },
          { type: "PestcontrolMonitors", id: "LIST" },
        ],
      }),
      pestcontrolMonitorShow: build.query<
        PestcontrolMonitor,
        ShowPestcontrolMonitorRequest
      >({
        query: (params) => ({
          url: "/pestcontrol/monitors/get",
          method: "GET",
          params,
        }),
        providesTags: (result) =>
          result ? [{ type: "PestcontrolMonitor", id: result.id }] : [],
      }),
      pestcontrolMonitorDelete: build.mutation<
        void,
        DeletePestcontrolMonitorRequest
      >({
        query: (params) => ({
          url: "/pestcontrol/monitors",
          method: "DELETE",
          params,
        }),
        invalidatesTags: [{ type: "PestcontrolMonitors", id: "LIST" }],
      }),
      pestcontrolMonitorImage: build.mutation<
        PestcontrolMonitor,
        UploadPestcontrolMonitorImageRequest
      >({
        query: (request) => {
          const body = new FormData();
          body.append("monitorId", request.monitorId);
          body.append("fileId", request.fileId);
          body.append("file", request.file || "");
          return {
            url: "/pestcontrol/monitors/image",
            method: "POST",
            body,
            formData: true,
          };
        },
        invalidatesTags: (_result, _error, request) => [
          { type: "PestcontrolMonitor", id: request.monitorId },
          { type: "PestcontrolMonitors", id: "LIST" },
        ],
      }),
      pestcontrolMonitorDeleteImage: build.mutation<
        PestcontrolMonitor,
        DeletePestcontrolMonitorImageRequest
      >({
        query: (body) => ({
          url: "/pestcontrol/monitors/image",
          method: "DELETE",
          body,
        }),
        invalidatesTags: (_result, _error, request) => [
          { type: "PestcontrolMonitor", id: request.monitorId },
          { type: "PestcontrolMonitors", id: "LIST" },
        ],
      }),
      pestcontrolMonitorShowImage: build.query<
        { objectURL: string },
        ShowPestcontrolMonitorImageRequest
      >({
        query: (request) => ({
          url: "/pestcontrol/monitors/image",
          method: "GET",
          params: request,
          responseHandler: async (response) => {
            if (response.status !== 200) {
              return response.json();
            }
            return {
              objectURL: window.URL.createObjectURL(await response.blob()),
            };
          },
          cache: "no-cache",
        }),
      }),
      pestcontrolMonitorList: build.query<
        PestcontrolMonitorListResponse,
        PestcontrolMonitorListRequest
      >({
        query: (params) => ({
          url: "/pestcontrol/monitors/list",
          method: "GET",
          params: base64ListRequest(params),
        }),
        providesTags: (result) =>
          result
            ? [
                { type: "PestcontrolMonitors", id: "LIST" },
                ...result.items.map((monitor) => ({
                  type: "PestcontrolMonitor" as const,
                  id: monitor.id,
                })),
              ]
            : [{ type: "PestcontrolMonitors", id: "LIST" }],
      }),
    }),
  });

export const {
  usePestcontrolMonitorPutMutation,
  usePestcontrolMonitorShowQuery,
  usePestcontrolMonitorDeleteMutation,
  usePestcontrolMonitorImageMutation,
  usePestcontrolMonitorDeleteImageMutation,
  usePestcontrolMonitorShowImageQuery,
  usePestcontrolMonitorListQuery,
} = pestcontrolMonitorApi;
